import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useRegistration } from '../../../contexts/RegistrationContext';
import { getCurrentApiEndpoints } from '../../../config/api';

const StepContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: #000;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: #666;
  margin-bottom: 2rem;
`;

const ImportantNote = styled.div`
  background: #d1ecf1;
  border: 1px solid #bee5eb;
  border-radius: 15px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #0c5460;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  border: 1px solid #ddd;
  border-radius: 15px;
  padding: 1.5rem;
  background: #f8f9fa;
`;

const SectionTitle = styled.h3`
  color: #000;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
`;

const OwnerCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 15px;
  padding: 1.5rem;
  background: white;
  margin-bottom: 1rem;
`;

const OwnerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
`;

const OwnerTitle = styled.h4`
  color: #000;
  margin: 0;
  font-weight: 600;
`;

const RemoveButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #c82333;
    transform: translateY(-1px);
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #000;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
  
  /* Hide spinner buttons for number inputs */
  &[type="number"] {
    -moz-appearance: textfield;
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const FileUpload = styled.div<{ disabled?: boolean }>`
  border: 2px dashed #ddd;
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    border-color: ${props => props.disabled ? '#ddd' : '#000'};
  }
  
  &.dragover {
    border-color: #000;
    background-color: #f8f9ff;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #000;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const UploadStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  color: #000;
  font-weight: 500;
`;

const AddOwnerButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  margin-top: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #218838;
    transform: translateY(-1px);
  }
`;

const CapitalSection = styled.div`
  border: 1px solid #ddd;
  border-radius: 15px;
  padding: 1.5rem;
  background: white;
`;

const TotalSection = styled.div`
  background: #e9ecef;
  padding: 1rem;
  border-radius: 15px;
  margin-top: 1rem;
  text-align: center;
  font-weight: bold;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.primary {
    background: #000;
    color: white;
    
    &:hover:not(:disabled) {
      background: #333;
      transform: translateY(-1px);
    }
  }
  
  &.secondary {
    background: #6c757d;
    color: white;
    
    &:hover:not(:disabled) {
      background: #5a6268;
      transform: translateY(-1px);
    }
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

interface Owner {
  full_name: string;
  address: string;
  passport_scan_path?: string;
  additional_proof_path?: string;
  share_percentage: number | string;
}

const OwnersInfo: React.FC = () => {
  const { registrationData, setCurrentStep, saveOwnersInfo, uploadDocument, updateRegistrationData } = useRegistration();
  
  // Initialize with default owner if no data exists
  const getInitialOwners = (): Owner[] => {
    if (!registrationData.owners_data) {
      return [{ full_name: '', address: '', share_percentage: 100 }];
    }
    
    try {
      const parsed = JSON.parse(registrationData.owners_data);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : [{ full_name: '', address: '', share_percentage: 100 }];
    } catch (error) {
      console.error('Failed to parse owners data:', error);
      return [{ full_name: '', address: '', share_percentage: 100 }];
    }
  };
    
  const [owners, setOwners] = useState<Owner[]>(getInitialOwners());
  const [authorizedCapital, setAuthorizedCapital] = useState(registrationData.authorized_capital || 10000);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<{[key: string]: boolean}>({});

  // Sync local state with registration context data when it changes
  useEffect(() => {
    const newOwners = getInitialOwners();
    setOwners(newOwners);
    setAuthorizedCapital(registrationData.authorized_capital || 10000);
  }, [registrationData.owners_data, registrationData.authorized_capital]);

  const addOwner = () => {
    setOwners([...owners, { full_name: '', address: '', share_percentage: 0 }]);
  };

  const removeOwner = (index: number) => {
    if (owners.length > 1) {
      setOwners(owners.filter((_, i) => i !== index));
    }
  };

  const updateOwner = (index: number, field: keyof Owner, value: string | number) => {
    const updated = owners.map((owner, i) => 
      i === index ? { ...owner, [field]: value } : owner
    );
    setOwners(updated);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, ownerIndex: number, documentType: string) => {
    const file = e.target.files?.[0];
    if (file) {
      const uploadKey = `${ownerIndex}-${documentType}`;
      
      try {
        // Set loading state
        setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));
        
        await uploadDocument(file, ownerIndex, documentType);
        // Update the owner with the file name for immediate UI feedback
        const fieldName = documentType === 'passport' ? 'passport_scan_path' : 'additional_proof_path';
        updateOwner(ownerIndex, fieldName as keyof Owner, file.name);
        
        // Reload registration data to get the updated file paths from the server
        if (registrationData.id) {
          const response = await axios.get(getCurrentApiEndpoints().REGISTRATION_BY_ID(registrationData.id!));
          updateRegistrationData(response.data);
        }
      } catch (error) {
        console.error('Failed to upload document:', error);
        alert('Failed to upload document. Please try again.');
      } finally {
        // Clear loading state
        setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
      }
    }
  };

  const getDisplayFileName = (ownerIndex: number, documentType: string): string | null => {
    const owner = owners[ownerIndex];
    const localPath = documentType === 'passport' ? owner.passport_scan_path : owner.additional_proof_path;
    
    if (localPath) {
      return localPath;
    }
    
    // Check original data if local state doesn't have the file
    try {
      if (registrationData.owners_data) {
        const originalOwners = JSON.parse(registrationData.owners_data);
        const originalOwner = originalOwners[ownerIndex];
        if (originalOwner) {
          return documentType === 'passport' ? originalOwner.passport_scan_path : originalOwner.additional_proof_path;
        }
      }
    } catch (error) {
      console.error('Failed to parse original owners data for display:', error);
    }
    
    return null;
  };

  const isFileUploading = (ownerIndex: number, documentType: string): boolean => {
    const uploadKey = `${ownerIndex}-${documentType}`;
    return uploadingFiles[uploadKey] || false;
  };

  const getTotalPercentage = () => {
    return owners.reduce((total, owner) => {
      const percentage = typeof owner.share_percentage === 'string' 
        ? parseFloat(owner.share_percentage) || 0
        : owner.share_percentage || 0;
      return total + percentage;
    }, 0);
  };

  const validateForm = () => {
    const newErrors: string[] = [];
    
    // Get the original owners data to check for existing uploaded documents
    let originalOwners: Owner[] = [];
    try {
      if (registrationData.owners_data) {
        originalOwners = JSON.parse(registrationData.owners_data);
      }
    } catch (error) {
      console.error('Failed to parse original owners data:', error);
    }
    
    // Check if all owners have required fields
    owners.forEach((owner, index) => {
      if (!owner.full_name.trim()) {
        newErrors.push(`Owner ${index + 1}: Full name is required`);
      }
      if (!owner.address.trim()) {
        newErrors.push(`Owner ${index + 1}: Address is required`);
      }
      
      // Check share percentage
      const sharePercentage = typeof owner.share_percentage === 'string' 
        ? parseFloat(owner.share_percentage) 
        : owner.share_percentage;
      if (!owner.share_percentage || isNaN(sharePercentage) || sharePercentage <= 0) {
        newErrors.push(`Owner ${index + 1}: Share percentage is required and must be a valid number`);
      }
      
      // Check for passport scan - either in current state or original data
      const hasPassportScan = owner.passport_scan_path || 
                             (originalOwners[index] && originalOwners[index].passport_scan_path);
      if (!hasPassportScan) {
        newErrors.push(`Owner ${index + 1}: Passport scan is required`);
      }
    });
    
    // Check total percentage
    const totalPercentage = getTotalPercentage();
    if (totalPercentage !== 100) {
      newErrors.push(`Total share percentage must equal 100% (currently ${totalPercentage}%)`);
    }
    
    // Check authorized capital
    if (authorizedCapital < 10000) {
      newErrors.push('Authorized capital must be at least 10,000 AMD');
    }
    
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Convert string share_percentage values to numbers before saving
      const ownersWithNumbers = owners.map(owner => ({
        ...owner,
        share_percentage: typeof owner.share_percentage === 'string' 
          ? parseFloat(owner.share_percentage) || 0
          : owner.share_percentage
      }));
      
      await saveOwnersInfo({
        owners_data: JSON.stringify(ownersWithNumbers),
        authorized_capital: authorizedCapital,
      });
      setCurrentStep(4);
    } catch (error) {
      console.error('Failed to save owners information:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(2);
  };

  return (
    <StepContainer>
      <Title>Step 3: Owners Information and Share Distribution</Title>
      <Description>
        Add all company owners and their share percentages. Each owner must provide their personal information and required documents.
      </Description>
      
      <ImportantNote>
        <strong>Important:</strong>
        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
          <li>Armenian translation will be officially registered</li>
          <li>Passport scan is required for licensed translation</li>
          <li>If passport doesn't include residential address, additional proof will be required (bank statement, utility bill, or official registry extract)</li>
          <li>Total share percentage must equal 100%</li>
        </ul>
      </ImportantNote>
      
      <Form onSubmit={handleSubmit}>
        <Section>
          <SectionTitle>Company Owners</SectionTitle>
          
          {owners.map((owner, index) => (
            <OwnerCard key={index}>
              <OwnerHeader>
                <OwnerTitle>Owner {index + 1}</OwnerTitle>
                {owners.length > 1 && (
                  <RemoveButton type="button" onClick={() => removeOwner(index)}>
                    Remove Owner
                  </RemoveButton>
                )}
              </OwnerHeader>
              
              <FormGroup>
                <Label>Full Name (Required) *</Label>
                <Input
                  type="text"
                  placeholder="Enter full name as shown in passport..."
                  value={owner.full_name}
                  onChange={(e) => updateOwner(index, 'full_name', e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Permanent Residential Address (Required) *</Label>
                <TextArea
                  placeholder="Enter complete permanent residential address..."
                  value={owner.address}
                  onChange={(e) => updateOwner(index, 'address', e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Passport Scan (Required) *</Label>
                <FileUpload 
                  disabled={isFileUploading(index, 'passport')}
                  onClick={() => !isFileUploading(index, 'passport') && document.getElementById(`passport-upload-${index}`)?.click()}
                >
                  {isFileUploading(index, 'passport') ? (
                    <UploadStatus>
                      <LoadingSpinner />
                      Uploading passport scan...
                    </UploadStatus>
                  ) : getDisplayFileName(index, 'passport') ? (
                    <div>
                      <p>✓ {getDisplayFileName(index, 'passport')}</p>
                      <small>Click to change file</small>
                    </div>
                  ) : (
                    <div>
                      <p>📄 Click to upload passport scan</p>
                      <small>Supported formats: JPG, PNG, PDF</small>
                    </div>
                  )}
                </FileUpload>
                <FileInput
                  id={`passport-upload-${index}`}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange(e, index, 'passport')}
                  required={!getDisplayFileName(index, 'passport')}
                  disabled={isFileUploading(index, 'passport')}
                />
              </FormGroup>

              <FormGroup>
                <Label>Additional Proof Document (Optional)</Label>
                <FileUpload 
                  disabled={isFileUploading(index, 'additional_proof')}
                  onClick={() => !isFileUploading(index, 'additional_proof') && document.getElementById(`proof-upload-${index}`)?.click()}
                >
                  {isFileUploading(index, 'additional_proof') ? (
                    <UploadStatus>
                      <LoadingSpinner />
                      Uploading additional proof...
                    </UploadStatus>
                  ) : getDisplayFileName(index, 'additional_proof') ? (
                    <div>
                      <p>✓ {getDisplayFileName(index, 'additional_proof')}</p>
                      <small>Click to change file</small>
                    </div>
                  ) : (
                    <div>
                      <p>📄 Click to upload additional proof</p>
                      <small>Bank statement, utility bill, or official registry extract</small>
                    </div>
                  )}
                </FileUpload>
                <FileInput
                  id={`proof-upload-${index}`}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange(e, index, 'additional_proof')}
                  disabled={isFileUploading(index, 'additional_proof')}
                />
              </FormGroup>

              <FormGroup>
                <Label>Share Percentage (%) (Required) *</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="Enter share percentage..."
                  value={owner.share_percentage === '' ? '' : owner.share_percentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateOwner(index, 'share_percentage', value);
                  }}
                  required
                />
              </FormGroup>
            </OwnerCard>
          ))}
          
          <AddOwnerButton type="button" onClick={addOwner}>
            + Add Another Owner
          </AddOwnerButton>
          
          <TotalSection>
            Total Share Percentage: {getTotalPercentage()}%
            {getTotalPercentage() !== 100 && (
              <ErrorMessage style={{ marginTop: '0.5rem' }}>
                Must equal 100%
              </ErrorMessage>
            )}
          </TotalSection>
        </Section>

        <CapitalSection>
          <SectionTitle>Authorized Capital</SectionTitle>
          <FormGroup>
            <Label>Authorized Capital (AMD) (Required) *</Label>
            <Input
              type="number"
              min="10000"
              step="1000"
              placeholder="Minimum 100,000 AMD"
              value={authorizedCapital}
              onChange={(e) => setAuthorizedCapital(parseFloat(e.target.value) || 0)}
              required
            />
            <small style={{ color: '#666' }}>
              Minimum authorized capital is 100,000 AMD for limited liability companies
            </small>
          </FormGroup>
        </CapitalSection>

        {errors.length > 0 && (
          <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '1rem' }}>
            <strong style={{ color: '#721c24' }}>Please fix the following errors:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', color: '#721c24' }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <ButtonGroup>
          <Button type="button" className="secondary" onClick={handleBack}>
            Back: Company Name
          </Button>
          <Button type="submit" className="primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Next: Charter Details'}
          </Button>
        </ButtonGroup>
      </Form>
    </StepContainer>
  );
};

export default OwnersInfo;
